<template>
  <b-row class="mb-0 alerts-notifications-panel" :class="{ 'alerts-panel-compact': compact, 'alerts-panel-no-chart': hideChart }">
    <!-- Alerts Tabs with Tables (full width when chart hidden) -->
    <b-col :lg="hideChart ? 12 : 6" md="12" cols="12" :class="hideChart ? 'mb-0' : 'pr-lg-1 pr-md-0 mb-0'">
      <b-card
        no-body
        class="shadow-sm rounded-lg alerts-tabs-card"
        :style="compact ? 'height: 210px; overflow: hidden;' : 'height: 500px; overflow: hidden;'"
      >
        <b-tabs card content-class="p-0 h-100 d-flex flex-column" nav-class="border-bottom alerts-nav-dense" style="min-height: 0;">
          <!-- Team Leave Balance Tab (hidden when shown as mid-row panel on Team View) -->
          <b-tab v-if="!hideLeaveBalance">
            <template #title>
              <feather-icon icon="PieChartIcon" size="14" class="mr-1" />
              <b-badge v-if="teamLeaveBalance && teamLeaveBalance.length > 0" variant="info" class="ml-1">{{ teamLeaveBalance.length }}</b-badge>
            </template>
            <div class="p-0 h-100 d-flex flex-column">
              <div class="d-flex align-items-center" :class="compact ? 'mb-0' : 'mb-1'">
                <h6 class="mb-0 font-weight-bold flex-shrink-0">
                  Team Leave Balance
                  <small v-if="teamLeaveBalance && teamLeaveBalance.length > 0" class="text-muted ml-1">({{ filteredTeamLeaveBalance.length }} of {{ teamLeaveBalance.length }})</small>
                </h6>
                <b-form-input
                  v-if="teamLeaveBalance && teamLeaveBalance.length > 0"
                  v-model="leaveBalanceSearch"
                  size="sm"
                  placeholder="Search employee..."
                  class="mx-2"
                  :style="compact ? 'max-width: 140px;' : 'max-width: 200px;'"
                />
                <b-button
                  v-if="teamLeaveBalance && teamLeaveBalance.length > 0"
                  size="sm"
                  variant="outline-primary"
                  class="ml-auto flex-shrink-0"
                  @click="$emit('download-team-leave-balance')"
                >
                  <feather-icon icon="DownloadIcon" :size="compact ? '12' : '14'" />
                </b-button>
              </div>
              <div v-if="!teamLeaveBalance || teamLeaveBalance.length === 0" class="text-center text-muted py-1 flex-grow-1 d-flex flex-column justify-content-center alerts-empty-state">
                <feather-icon icon="UsersIcon" :size="compact ? '16' : '20'" class="mb-1 text-muted" />
                <p class="mb-0 small">No team leave data</p>
              </div>
              <div
                v-else
                class="flex-grow-1 alerts-table-scroll"
                :style="compact ? 'min-height: 0; max-height: 135px; overflow-y: auto;' : 'min-height: 0; max-height: 380px; overflow-y: auto;'"
              >
                <b-table
                  :items="filteredTeamLeaveBalance"
                  :fields="teamLeaveBalanceFields"
                  striped
                  hover
                  small
                  responsive
                  :per-page="effectiveLeaveBalancePerPage"
                  :current-page="leaveBalanceCurrentPage"
                >
                  <template #cell(employee)="row">
                    <b-link
                      class="d-flex align-items-center text-body cursor-pointer"
                      @click="showLeavesBreakupModal(row.item)"
                    >
                      <feather-icon icon="UserIcon" :size="compact ? '12' : '14'" class="mr-1 text-muted" />
                      <span class="text-primary">{{ getUserName(row.item.emp_id) }}</span>
                    </b-link>
                  </template>
                  <template #cell(leaveBalance)="row">
                    <b-badge variant="success">{{ roundToHalf(row.item.leaveBalance) }}</b-badge>
                  </template>
                  <template #cell(leavesApplied)="row">
                    <span>{{ roundToHalf(row.item.leavesApplied) }}</span>
                  </template>
                </b-table>
                <!-- Pagination -->
                <div v-if="filteredTeamLeaveBalance.length > effectiveLeaveBalancePerPage" class="d-flex justify-content-between align-items-center px-1 pb-1">
                  <small class="text-muted">Page {{ leaveBalanceCurrentPage }} of {{ Math.ceil(filteredTeamLeaveBalance.length / effectiveLeaveBalancePerPage) }}</small>
                  <b-pagination
                    v-model="leaveBalanceCurrentPage"
                    :total-rows="filteredTeamLeaveBalance.length"
                    :per-page="effectiveLeaveBalancePerPage"
                    size="sm"
                    class="mb-0"
                    pills
                  />
                </div>
              </div>
            </div>
          </b-tab>

          <!-- Leave Conflicts Tab -->
          <b-tab>
            <template #title>
              <feather-icon icon="UsersIcon" size="14" class="mr-1" />
              <b-badge v-if="leaveConflicts && leaveConflicts.length > 0" variant="danger" class="ml-1">{{ leaveConflicts.length }}</b-badge>
            </template>
            <div class="h-100 d-flex flex-column">
              <div class="d-flex justify-content-between align-items-center mb-0">
                <h6 class="mb-0 font-weight-bold">Upcoming Leave Conflicts</h6>
                <b-button
                  v-if="leaveConflicts && leaveConflicts.length > 0"
                  size="sm"
                  variant="outline-primary"
                  @click="$emit('download-leave-conflicts')"
                >
                  <feather-icon icon="DownloadIcon" size="14" />
                </b-button>
              </div>
              <div
                v-if="!leaveConflicts || leaveConflicts.length === 0"
                class="text-muted small px-1 py-50 alerts-empty-state alerts-empty-state--compact"
              >
                No upcoming leave conflicts
              </div>
              <div v-else class="flex-grow-1 overflow-auto" style="min-height: 0;">
                <b-table
                  :items="leaveConflicts"
                  :fields="leaveConflictsFields"
                  striped
                  hover
                  small
                  responsive
                >
                  <template #cell(date)="row">
                    <div class="d-flex align-items-center">
                      <feather-icon icon="CalendarIcon" size="14" class="mr-2 text-muted" />
                      <span>{{ formatDate(row.item.date) }}</span>
                    </div>
                  </template>
                  <template #cell(employees)="row">
                    <div class="d-flex flex-wrap">
                      <span
                        v-for="(emp, idx) in row.item.employees"
                        :key="emp.emp_id"
                        class="mr-2 mb-1"
                      >
                        {{ getUserName(emp.emp_id) }}<span v-if="idx < row.item.employees.length - 1">,</span>
                      </span>
                    </div>
                  </template>
                  <template #cell(count)="row">
                    <b-badge variant="danger">{{ row.item.employees.length }} employees</b-badge>
                  </template>
                </b-table>
              </div>
            </div>
          </b-tab>

          <!-- Probation Ending Tab -->
          <b-tab>
            <template #title>
              <feather-icon icon="UserCheckIcon" size="14" class="mr-1" />
              <b-badge v-if="probationEnding && probationEnding.length > 0" variant="info" class="ml-1">{{ probationEnding.length }}</b-badge>
            </template>
            <div class="p-0 h-100 d-flex flex-column">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <h6 class="mb-0 font-weight-bold">Probation Ending Soon</h6>
                <b-button
                  v-if="probationEnding && probationEnding.length > 0"
                  size="sm"
                  variant="outline-primary"
                  @click="$emit('download-probation-ending')"
                >
                  <feather-icon icon="DownloadIcon" size="14" />
                </b-button>
              </div>
              <div v-if="!probationEnding || probationEnding.length === 0" class="text-center text-muted py-2 flex-grow-1 d-flex flex-column justify-content-center alerts-empty-state">
                <feather-icon icon="CheckCircleIcon" size="20" class="mb-1 text-success" />
                <p class="mb-0 small">No probation periods ending soon</p>
              </div>
              <div v-else class="flex-grow-1 overflow-auto" style="min-height: 0;">
                <b-table
                  :items="probationEnding"
                  :fields="probationEndingFields"
                  striped
                  hover
                  small
                  responsive
                >
                  <template #cell(employee)="row">
                    <div class="d-flex align-items-center">
                      <feather-icon icon="UserCheckIcon" size="14" class="mr-2 text-muted" />
                      <span>{{ getUserName(row.item.emp_id) }}</span>
                    </div>
                  </template>
                  <template #cell(probationEndDate)="row">
                    <span>{{ formatDate(row.item.probationEndDate || row.item.probation_end_date) }}</span>
                  </template>
                  <template #cell(daysRemaining)="row">
                    <b-badge variant="info">{{ row.item.daysRemaining || row.item.days_remaining }} days</b-badge>
                  </template>
                </b-table>
              </div>
            </div>
          </b-tab>
          <!-- Punch In/Out Data Tab -->
          <b-tab v-if="viewMode === 'org'">
            <template #title>
              <feather-icon icon="WatchIcon" size="14" class="mr-1" />
            </template>
            <div class="p-0 h-100 d-flex flex-column">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <h6 class="mb-0 font-weight-bold">Punch In/Out Data</h6>
              </div>

              <!-- Quick-select range buttons -->
              <div class="d-flex flex-wrap mb-2" style="gap: 4px;">
                <b-button size="sm" variant="outline-secondary" @click="setPunchDateRange('week')">This Week</b-button>
                <b-button size="sm" variant="outline-secondary" @click="setPunchDateRange('month')">This Month</b-button>
                <b-button size="sm" variant="outline-secondary" @click="setPunchDateRange('lastMonth')">Last Month</b-button>
                <b-button size="sm" variant="outline-secondary" @click="setPunchDateRange('quarter')">This Quarter</b-button>
              </div>

              <!-- Manual From/To date inputs -->
              <b-row class="mb-1 no-gutters" style="gap: 0;">
                <b-col cols="6" class="pr-1">
                  <small class="font-weight-bold d-block mb-25">From</small>
                  <b-form-input type="date" v-model="punchDataStartDate" size="sm" />
                </b-col>
                <b-col cols="6" class="pl-1">
                  <small class="font-weight-bold d-block mb-25">To</small>
                  <b-form-input type="date" v-model="punchDataEndDate" size="sm" />
                </b-col>
              </b-row>

              <!-- Date range summary -->
              <p v-if="punchDataStartDate && punchDataEndDate" class="small text-muted mb-1">
                {{ formatPunchDateDisplay(punchDataStartDate) }} &rarr; {{ formatPunchDateDisplay(punchDataEndDate) }}
              </p>

              <!-- Employee filter -->
              <b-form-group class="mb-2" label-class="small font-weight-bold mb-25">
                <template #label><small class="font-weight-bold">Employee</small></template>
                <b-form-select
                  v-model="punchDataEmployeeFilter"
                  :options="punchDataEmployeeOptions"
                  size="sm"
                />
              </b-form-group>

              <!-- Download button -->
              <b-button
                variant="primary"
                size="sm"
                class="mt-auto"
                :disabled="!punchDataStartDate || !punchDataEndDate || punchDataLoading"
                @click="downloadPunchData"
              >
                <b-spinner v-if="punchDataLoading" small class="mr-1" />
                <feather-icon v-else icon="DownloadIcon" size="14" class="mr-1" />
                Download CSV
              </b-button>
            </div>
          </b-tab>
        </b-tabs>
      </b-card>
    </b-col>

    <!-- Leaves Breakup Modal -->
    <b-modal
      v-model="leavesBreakupModalVisible"
      title="Leaves - Breakup"
      hide-footer
      size="md"
      scrollable
      header-class="border-bottom py-2"
      body-class="p-3"
    >
      <div v-if="selectedEmployeeBreakup">
        <div class="d-flex align-items-center mb-2 pb-2 border-bottom">
          <feather-icon icon="UserIcon" size="18" class="mr-2 text-primary" />
          <h6 class="mb-0 font-weight-bold text-dark">{{ getUserName(selectedEmployeeBreakup.emp_id) }}</h6>
        </div>

        <!-- Leave Summary (balance + applied) -->
        <b-card no-body class="shadow-sm border">
          <b-card-body class="p-2">
            <div class="d-flex justify-content-between align-items-center flex-wrap mb-1">
              <h6 class="mb-0 font-weight-bold text-primary small">
                <feather-icon icon="PieChartIcon" size="14" class="mr-1 align-middle" />
                Leave Summary
              </h6>
              <div class="d-flex align-items-center flex-wrap">
                <b-badge variant="success" class="px-2 mr-50">
                  Avail: {{ roundToHalf(selectedEmployeeBreakup.leaveBalance) }} days
                </b-badge>
                <b-badge variant="info" class="px-2">
                  Used: {{ roundToHalf(selectedEmployeeBreakup.leavesApplied) }} days
                </b-badge>
              </div>
            </div>
            <b-table
              v-if="leaveSummaryRows.length > 0"
              :items="leaveSummaryRows"
              :fields="leaveSummaryFields"
              small
              striped
              bordered
              thead-class="leave-summary-table__thead"
              class="mb-0 leave-summary-table"
            >
              <template #cell(label)="row">
                {{ row.item.label || '-' }}
              </template>
              <template #cell(available)="row">
                <span :class="{ 'text-muted': !(row.item.available > 0) }">
                  {{ row.item.available > 0 ? roundToHalf(row.item.available) : '—' }}
                </span>
              </template>
              <template #cell(used)="row">
                {{ row.item.used > 0 ? roundToHalf(row.item.used) : 0 }}
              </template>
            </b-table>
            <p v-else class="text-muted small mb-0">No leave data available</p>
          </b-card-body>
        </b-card>
      </div>
    </b-modal>

    <!-- Leave Balance Overview Chart (hidden in compact Team View — table tab covers this) -->
    <b-col v-if="!hideChart" lg="6" md="12" cols="12" class="pl-lg-1 pl-md-0 mb-2">
      <b-card no-body class="shadow-sm rounded-lg p-3" style="height: 500px;">
        <div class="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
          <h5 class="mb-0 font-weight-bold">Leave Balance Overview</h5>
        </div>
        <div v-if="loading" class="text-center py-5 flex-grow-1 d-flex align-items-center justify-content-center">
          <b-spinner variant="primary" />
        </div>
        <div v-else class="flex-grow-1">
          <apexchart
            type="bar"
            height="400"
            :options="leaveBalanceChartOptions"
            :series="leaveBalanceChartSeries"
          />
        </div>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCardBody,
  BTabs,
  BTab,
  BBadge,
  BButton,
  BTable,
  BSpinner,
  BModal,
  BLink,
  BFormInput,
  BFormGroup,
  BFormSelect,
  BPagination,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import attendanceService from '@/services/attendance-service'

export default {
  name: 'AlertsNotificationsPanel',
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BTabs,
    BTab,
    BBadge,
    BButton,
    BTable,
    BSpinner,
    BModal,
    BLink,
    BFormInput,
    BFormGroup,
    BFormSelect,
    BPagination,
    apexchart: VueApexCharts,
  },
  data() {
    return {
      leavesBreakupModalVisible: false,
      selectedEmployeeBreakup: null,
      leaveBalanceSearch: '',
      leaveBalanceCurrentPage: 1,
      leaveBalancePerPage: 15,
      punchDataStartDate: '',
      punchDataEndDate: '',
      punchDataEmployeeFilter: null,
      punchDataLoading: false,
      leaveSummaryFields: [
        { key: 'label', label: 'Leave Type', sortable: false },
        { key: 'available', label: 'Available (Days)', sortable: false, thClass: 'text-right', tdClass: 'text-right' },
        { key: 'used', label: 'Used (Days)', sortable: false, thClass: 'text-right', tdClass: 'text-right' },
      ],
    }
  },
  computed: {
    effectiveLeaveBalancePerPage() {
      return this.compact ? 8 : this.leaveBalancePerPage
    },
    punchDataEmployeeOptions() {
      const options = [{ value: null, text: 'All Employees' }]
      if (this.teamMembers && this.teamMembers.length > 0) {
        this.teamMembers.forEach(member => {
          const id = member.emp_id || member.id
          if (id) {
            options.push({ value: id, text: this.getUserName(id) || id })
          }
        })
      }
      return options
    },
    filteredTeamLeaveBalance() {
      if (!this.teamLeaveBalance || this.teamLeaveBalance.length === 0) return []
      if (!this.leaveBalanceSearch || !this.leaveBalanceSearch.trim()) return this.teamLeaveBalance
      const q = this.leaveBalanceSearch.trim().toLowerCase()
      return this.teamLeaveBalance.filter(item => {
        const name = (this.getUserName(item.emp_id) || item.emp_id || '').toLowerCase()
        return name.includes(q)
      })
    },
    leaveSummaryRows() {
      if (!this.selectedEmployeeBreakup || !this.selectedEmployeeBreakup.breakdown) return []
      return this.selectedEmployeeBreakup.breakdown
        .map(item => ({
          label: item.label || item.type || '-',
          available: this.getAvailableDays(item),
          used: this.getUsedDays(item),
        }))
        .filter(item => item.available > 0 || item.used > 0)
    },
  },
  watch: {
    leaveBalanceSearch() {
      this.leaveBalanceCurrentPage = 1
    },
  },
  created() {
    const today = new Date()
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
    const fmt = d => d.toISOString().split('T')[0]
    this.punchDataStartDate = fmt(firstDay)
    this.punchDataEndDate = fmt(today)
  },
  methods: {
    setPunchDateRange(range) {
      const today = new Date()
      const fmt = d => d.toISOString().split('T')[0]
      if (range === 'week') {
        const day = today.getDay()
        const diffToMonday = day === 0 ? -6 : 1 - day
        const monday = new Date(today)
        monday.setDate(today.getDate() + diffToMonday)
        this.punchDataStartDate = fmt(monday)
        this.punchDataEndDate = fmt(today)
      } else if (range === 'month') {
        this.punchDataStartDate = fmt(new Date(today.getFullYear(), today.getMonth(), 1))
        this.punchDataEndDate = fmt(today)
      } else if (range === 'lastMonth') {
        this.punchDataStartDate = fmt(new Date(today.getFullYear(), today.getMonth() - 1, 1))
        this.punchDataEndDate = fmt(new Date(today.getFullYear(), today.getMonth(), 0))
      } else if (range === 'quarter') {
        const q = Math.floor(today.getMonth() / 3)
        this.punchDataStartDate = fmt(new Date(today.getFullYear(), q * 3, 1))
        this.punchDataEndDate = fmt(today)
      }
    },
    formatPunchDateDisplay(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr + 'T00:00:00')
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${String(d.getDate()).padStart(2, '0')} ${months[d.getMonth()]} ${d.getFullYear()}`
    },
    exportToCsv(filename, rows) {
      if (!rows.length) return
      const headers = Object.keys(rows[0])
      const csv = [
        headers.join(','),
        ...rows.map(r => headers.map(h => JSON.stringify(r[h] != null ? r[h] : '')).join(',')),
      ].join('\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
    },
    async downloadPunchData() {
      if (!this.punchDataStartDate || !this.punchDataEndDate) return
      this.punchDataLoading = true
      try {
        const res = await attendanceService.getPunchAttendanceByDateRange(
          this.punchDataStartDate,
          this.punchDataEndDate,
          this.punchDataEmployeeFilter || null,
        )
        if (res && res.status === 'OK' && res.data && res.data.length > 0) {
          const rows = res.data.map(record => ({
            'Employee ID': record.emp_id || '',
            'Employee Name': this.getUserName(record.emp_id) || record.emp_id || '',
            Date: record.date || '',
            'Punch-In Time': record.intime || '',
            'Punch-Out Time': record.outtime || '',
            'Work Hours': record.worktime || '',
            Status: record.attendance || '',
          }))
          this.exportToCsv(
            `Punch_Data_${this.punchDataStartDate}_to_${this.punchDataEndDate}.csv`,
            rows,
          )
        } else {
          this.$bvToast.toast('No punch data found for the selected range.', {
            title: 'No Data',
            variant: 'warning',
            solid: true,
          })
        }
      } catch (err) {
        console.error('Error downloading punch data:', err)
        this.$bvToast.toast('Error fetching punch data. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.punchDataLoading = false
      }
    },
    showLeavesBreakupModal(item) {
      this.selectedEmployeeBreakup = item
      this.leavesBreakupModalVisible = true
    },
    getUsedDays(item) {
      if (item.used != null && item.used !== '') return item.used
      if (item.Used != null && item.Used !== '') return item.Used
      if (item.used_days != null && item.used_days !== '') return item.used_days
      return 0
    },
    getAvailableDays(item) {
      if (item.available != null && item.available !== '') return Number(item.available)
      if (item.value != null && item.value !== '') return Number(item.value)
      return 0
    },
  },
  props: {
    leaveConflicts: {
      type: Array,
      default: () => [],
    },
    probationEnding: {
      type: Array,
      default: () => [],
    },
    teamLeaveBalance: {
      type: Array,
      default: () => [],
    },
    teamMembers: {
      type: Array,
      default: () => [],
    },
    teamLeaveBalanceFields: {
      type: Array,
      default: () => [
        { key: 'employee', label: 'Employee', sortable: true },
        { key: 'leaveBalance', label: 'Leave Balance', sortable: true },
        { key: 'leavesApplied', label: 'Leaves Taken', sortable: true },
      ],
    },
    leaveConflictsFields: {
      type: Array,
      default: () => [],
    },
    probationEndingFields: {
      type: Array,
      default: () => [],
    },
    leaveBalanceChartOptions: {
      type: Object,
      default: () => ({}),
    },
    leaveBalanceChartSeries: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    getUserName: {
      type: Function,
      required: true,
    },
    formatDate: {
      type: Function,
      required: true,
    },
    roundToHalf: {
      type: Function,
      required: true,
    },
    viewMode: {
      type: String,
      default: 'my',
    },
    hideChart: {
      type: Boolean,
      default: false,
    },
    hideLeaveBalance: {
      type: Boolean,
      default: false,
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },
}
</script>

<style scoped>
/* Ensure tab content and panes pass height for scroll */
.card >>> .tabs {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.card >>> .tab-content {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.card >>> .tab-pane {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.card >>> .tab-pane.active {
  display: flex;
}

.alerts-panel-compact .alerts-tabs-card {
  border: 1px solid #e8ecf0;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04) !important;
  border-radius: 10px !important;
}

.alerts-panel-no-chart {
  margin-bottom: 0 !important;
}

.alerts-empty-state {
  min-height: 120px;
  max-height: 180px;
}

.alerts-panel-compact .alerts-empty-state {
  min-height: 28px;
  max-height: 40px;
}

.alerts-empty-state--compact {
  min-height: 0 !important;
  max-height: none !important;
  display: block;
  line-height: 1.3;
}

.alerts-panel-compact >>> .nav-tabs .nav-link {
  padding: 0.3rem 0.5rem;
  font-size: 0.72rem;
}

.alerts-panel-compact >>> .card-header {
  padding: 0.25rem 0.4rem 0;
}

.alerts-panel-compact >>> .table th,
.alerts-panel-compact >>> .table td {
  padding: 0.28rem 0.4rem;
  font-size: 0.72rem;
}

.alerts-panel-compact h6 {
  font-size: 0.75rem;
}

.leave-summary-table {
  font-size: 0.72rem;
  background: #fff;
}

.leave-summary-table >>> .leave-summary-table__thead th,
.leave-summary-table >>> thead th {
  background: #e6f4f1;
  border-bottom-width: 1px;
  font-weight: 700;
  font-size: 0.68rem;
  color: #0f766e;
  white-space: nowrap;
  padding: 0.28rem 0.45rem;
}

.leave-summary-table >>> td {
  padding: 0.28rem 0.45rem;
  vertical-align: middle;
}
</style>
